/**
 * Shared parsing utilities for ingestion job results.
 *
 * Both useIngestionHydration (one-shot rehydration on page refresh) and
 * useValidationAndProfiling (active polling during validation) need to
 * interpret the same backend job response structures. These functions
 * centralise that logic so changes to the API schema only need updating
 * in one place.
 */
import {
  DataProfile,
  mapTabularProfileToDataProfile,
} from "datasets/pages/tables/components/file-validation/types";
import {
  DataIngestionResponse,
  JobResponse,
  TaskResult,
  TabularProfileTaskMessage,
  type FileTypeValidationTaskMessage,
  type FileRowValidationTaskMessage,
} from "datasets/pages/tables/types/interfaces/ingestion-job";

// ---------------------------------------------------------------------------
// TODO(SCDM-2560): Remove this helper once the backend always returns parsed objects.
// https://manifoldai.atlassian.net/browse/SCDM-2560
// ---------------------------------------------------------------------------

/**
 * Safely parse job results.data, handling both a JSON string (current API)
 * and a pre-parsed object (upcoming API change).
 *
 * This is the JSON deserialization boundary — the `as Record<string, unknown>`
 * cast is unavoidable here since we're converting untyped JSON into our domain types.
 */
export function parseResultsData(data: unknown): Record<string, unknown> | null {
  if (data == null) {
    return null;
  }
  // Already a parsed object from the API
  if (typeof data === "object") {
    return data as Record<string, unknown>;
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validation job parsing
// ---------------------------------------------------------------------------

export interface ParsedValidationResults {
  /** Path to the _VALID_ROWS file produced by row validation */
  validatedFilePath: string | null;
  /** Detected file type (e.g. "csv", "tsv"), lowercased */
  fileType: string | null;
  /** Number of invalid rows reported by row validation */
  errorRows: number;
  /** Whether any validation task has state === "FAILED" */
  hasErrors: boolean;
  /** Whether any validation task has state === "WARNING" */
  hasWarnings: boolean;
  /** The raw tasks array (null if parsing failed or no tasks present) */
  tasks: TaskResult[] | null;
}

/**
 * Parse a validation job's results.data JSON and extract the core fields
 * that both hooks need: file type, validated file path, error counts,
 * and status flags.
 *
 * Returns the raw `tasks` array so consumers can do additional UI-specific
 * mapping (e.g. building ValidationJob[] cards) without re-parsing.
 */
export function parseValidationJobResults(job: JobResponse): ParsedValidationResults {
  const defaults: ParsedValidationResults = {
    validatedFilePath: null,
    fileType: null,
    errorRows: 0,
    hasErrors: false,
    hasWarnings: false,
    tasks: null,
  };

  const parsed = parseResultsData(job.results?.data);
  if (!parsed) {
    return defaults;
  }

  // Job failed with an error message but no tasks
  if (!parsed.tasks && parsed.error) {
    return { ...defaults, hasErrors: true };
  }

  if (!parsed.tasks) {
    return defaults;
  }

  // Boundary cast: parsed.tasks comes from JSON deserialization and conforms
  // to the TaskResult[] shape defined by the backend contract.
  const tasks = parsed.tasks as TaskResult[];

  const fileTypeTask = tasks.find(
    (t): t is TaskResult & { message: FileTypeValidationTaskMessage } =>
      t.message.task_name === "FileTypeValidationTaskMessage"
  );
  const rowTask = tasks.find(
    (t): t is TaskResult & { message: FileRowValidationTaskMessage } =>
      t.message.task_name === "FileRowValidationTaskMessage"
  );

  return {
    validatedFilePath: rowTask?.message.valid_rows_filepath ?? null,
    fileType: fileTypeTask?.message.file_type?.toLowerCase() ?? null,
    errorRows: rowTask?.message.invalid_row_count ?? 0,
    hasErrors: tasks.some((t) => t.state === "FAILED"),
    hasWarnings: tasks.some((t) => t.state === "WARNING"),
    tasks,
  };
}

// ---------------------------------------------------------------------------
// Profiling job parsing
// ---------------------------------------------------------------------------

/**
 * Parse a profiling job's results.data JSON and produce a DataProfile.
 *
 * Returns null when the job failed, has no results, or the results
 * cannot be parsed. Consumers handle their own error state management
 * (e.g. ProfilingError, isProfiling flags).
 */
export function parseProfilingJobResults(job: JobResponse, errorRows: number): DataProfile | null {
  const parsed = parseResultsData(job.results?.data);
  if (!parsed) {
    return null;
  }
  if (parsed.state === "FAILED" || parsed.error) {
    return null;
  }
  if (!parsed.message) {
    return null;
  }

  // Boundary cast: parsed.message comes from JSON deserialization and conforms
  // to the TabularProfileTaskMessage shape defined by the backend contract.
  const message = parsed.message as TabularProfileTaskMessage;
  return mapTabularProfileToDataProfile(message, errorRows);
}

// ---------------------------------------------------------------------------
// Source file URI recovery
// ---------------------------------------------------------------------------

/**
 * Recover the original source file URI from job results.
 *
 * Fallback chain:
 *   1. Profiling job results: `parsed.file`
 *   2. Validation job results: `parsed.tasks[0].file`
 *   3. Any job's arguments: `job.arguments.file_uris[0]`
 *   4. Ingestion record: `ingestion.source_file`
 */
export function recoverSourceFileUri(
  jobs: JobResponse[],
  ingestion?: DataIngestionResponse
): string | null {
  const profilingJob = jobs.find((j) => j.job_type === "profiling");
  const validationJob = jobs.find((j) => j.job_type === "validation");

  // Try profiling job results first (simpler structure).
  // Boundary casts: these fields are untyped extras on the JSON envelope,
  // not part of the TaskResult contract, so we cast at the boundary.
  const profilingParsed = parseResultsData(profilingJob?.results?.data);
  if (profilingParsed?.file && typeof profilingParsed.file === "string") {
    return profilingParsed.file;
  }

  // Fall back to validation job results
  const validationParsed = parseResultsData(validationJob?.results?.data);
  const tasksArray = validationParsed?.tasks as Array<Record<string, unknown>> | undefined;
  const firstTaskFile = tasksArray?.[0]?.file;
  if (firstTaskFile && typeof firstTaskFile === "string") {
    return firstTaskFile;
  }

  // Fall back to job arguments
  for (const job of jobs) {
    const fileUri = job.arguments?.file_uris?.[0];
    if (fileUri) {
      return fileUri;
    }
  }

  // Final fallback: ingestion record
  return ingestion?.source_file ?? null;
}

// ---------------------------------------------------------------------------
// S3 URI helpers
// ---------------------------------------------------------------------------

/**
 * Strip the s3://bucket/ prefix from a URI to get the plain object key.
 * If the value isn't an S3 URI, returns it as-is.
 * Accepts null/undefined for convenience and returns null in that case.
 */
export function stripS3Prefix(uri: string | null | undefined): string | null {
  if (!uri) {
    return null;
  }
  if (uri.startsWith("s3://")) {
    const withoutProtocol = uri.slice(5); // remove "s3://"
    const slashIndex = withoutProtocol.indexOf("/");
    return slashIndex !== -1 ? withoutProtocol.slice(slashIndex + 1) : withoutProtocol;
  }
  return uri;
}
